import { useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Card, Callout, Input } from "@inthememory/spectra-ui";

import { VusionLiveLogo } from "@/components/logos/VusionLiveLogo";
import { LoadingState } from "@/components/LoadingState";
import { useAuth } from "@/hooks/useAuth";
import { fetchRuntimeConfig } from "@/lib/runtime-config";

const KNOWN_AUTH_ERRORS = new Set([
  "access_denied",
  "invalid_request",
  "server_error",
  "temporarily_unavailable",
  "unauthorized_client",
  "unsupported_response_type",
]);

function sanitizeAuthParam(value: string | null, allowlist?: Set<string>): string | null {
  if (!value) return null;
  const trimmed = value.slice(0, 128).trim();
  if (allowlist && !allowlist.has(trimmed)) return null;
  return trimmed;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const runtimeConfigQuery = useQuery({
    queryKey: ["runtime-config"],
    queryFn: fetchRuntimeConfig,
    staleTime: Infinity,
  });
  const { isLoading, isAuthenticated } = useAuth();

  const searchParams = new URLSearchParams(window.location.search);
  const authError = sanitizeAuthParam(searchParams.get("auth_error"), KNOWN_AUTH_ERRORS);
  const authReason = sanitizeAuthParam(searchParams.get("reason"));
  const returnTo = searchParams.get("return_to") ?? "/";

  const loginPath = runtimeConfigQuery.data?.authLoginPath ?? "/auth/login";
  const baseLoginUrl = `${loginPath}?return_to=${encodeURIComponent(returnTo)}`;

  const form = useForm({
    defaultValues: {
      email: "",
    },
    onSubmit: async ({ value }) => {
      const loginHint = value.email.trim();
      const loginUrl = `${baseLoginUrl}&login_hint=${encodeURIComponent(loginHint)}`;
      window.location.assign(loginUrl);
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: "/", replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (isLoading) return <LoadingState />;

  return (
    <Card padding={10}>
      <div className="w-96 space-y-10">
        <div className="flex h-7 w-full items-center justify-center">
          <VusionLiveLogo />
        </div>

        <h4 className="text-center text-3xl text-slate-900">
          {t("auth.welcome")}
        </h4>

        {authError && (
          <Callout intent="error">
            <p>{t("auth.signInFailedDescription")}</p>
            <p className="mt-1 text-sm">
              {t("auth.errorCode", { code: authError })}
            </p>
            {authReason && (
              <p className="text-sm">
                {t("auth.errorReason", { reason: authReason })}
              </p>
            )}
          </Callout>
        )}

        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field
            name="email"
            validators={{
              onChange: ({ value }) => {
                const trimmed = value.trim();
                if (!trimmed) return "Email is required.";
                const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
                return isValidEmail ? undefined : "Please enter a valid email address.";
              },
            }}
          >
            {(field) => (
              <div className="space-y-2">
                <Input
                  width="full"
                  type="email"
                  autoFocus
                  autoComplete="email"
                  placeholder={t("auth.emailPlaceholder")}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(event) => field.handleChange(event.target.value)}
                />
                {field.state.meta.isTouched && field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-red-700">{field.state.meta.errors[0]}</p>
                )}
              </div>
            )}
          </form.Field>
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <Button width="full" type="submit" disabled={!canSubmit || isSubmitting}>
                {t("auth.continue")}
              </Button>
            )}
          </form.Subscribe>
        </form>
      </div>
    </Card>
  );
}
